import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Typography,
  Stack,
  InputAdornment,
  IconButton,
  Grid2,
} from '@mui/material';
import { toast } from 'sonner';
import { Iconify } from '../Minimal/src/components/iconify';
import { TablePaginationCustom } from '../Minimal/src/components/pagination';
import { useTable } from '../Minimal/src/hooks/use-table';
import Header from '../Header';
import Footer from '../Footer';
import { Link, useParams } from 'react-router-dom';
import { API_URL, BASE_URL } from '../Config';
import config from "../common/Config";
import * as XLSX from 'xlsx';
import '../common/commonStyles.css';

const StockTakeHistoryList = () => {
  const { stockId } = useParams();
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem("token");
  const [stockTakeHistory, setStockTakeHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const table = useTable({
    defaultRowsPerPage: 50,
    defaultDense: false,
    defaultOrder: 'desc',
    defaultOrderBy: 'name',
    defaultSelected: [],
    defaultCurrentPage: 0,
  });

  const fetchStockTakeHistory = async () => {
    const params = new URLSearchParams();
    params.append('draw', 1);
    params.append('start', 0);
    params.append('length', 50);

    const apiUrl = `${API_URL}/show_history_of_stock_take/${stockId}?${params.toString()}`;

    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stock take history');
      }
      const data = await response.json();
      setStockTakeHistory(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      toast.error('Failed to load stock take history');
      setStockTakeHistory([]);
    }
  };

  const filteredHistory = useMemo(() => {
    if (!searchQuery.trim()) {
      return stockTakeHistory;
    }
    const query = searchQuery.toLowerCase();
    return stockTakeHistory.filter((item) => {
      const name = (item.name || '').toLowerCase();
      const bay = (item.bay || '').toString().toLowerCase();
      const rack = (item.rack || '').toString().toLowerCase();
      const remark = (item.remark || '').toLowerCase();
      return (
        name.includes(query) ||
        bay.includes(query) ||
        rack.includes(query) ||
        remark.includes(query)
      );
    });
  }, [stockTakeHistory, searchQuery]);

  const handleExportExcel = () => {
    const tableData = filteredHistory.map(item => ({
      'Product Code - Name': item.name || '',
      'Bay': item.bay || '',
      'Rack': item.rack || '',
      'Sale P': item.price || '',
      'Cost P': item.cost || '',
      'Sys Qty': item.quantity || '',
      'Phy Qty': item.physical_qty || '',
      'Diff': item.diff || '',
      'Piece Qty': item.piece_qty || '',
      'Phy Piece Qty': item.physical_piece_qty || '',
      'Piece Diff': item.piece_diff || '',
      'Remark': item.remark || '',
    }));

    const ws = XLSX.utils.json_to_sheet(tableData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, 'StockTakeHistory.xlsx');
  };

  useEffect(() => {
    fetchStockTakeHistory();

    document.title = 'Stock Take History List';
    return () => {
      document.title = 'Ebusso';
    };
  }, [stockId]);

  useEffect(() => {
    table.onChangePage(null, 0);
  }, [searchQuery]);

  const renderRow = (item, index) => {
    const {
      name,
      bay,
      rack,
      price,
      cost,
      quantity,
      physical_qty,
      diff,
      piece_qty,
      physical_piece_qty,
      piece_diff,
      remark,
    } = item;

    return (
      <TableRow
        key={`${name}-${index}-${bay}-${rack}`}
        className="table-cell-style"
        sx={{
          "&:hover": {
            backgroundColor: "rgba(145, 158, 171, 0.04)",
          },
        }}
      >
        <TableCell sx={{ width: "250px", minWidth: "250px" }}>{name || ''}</TableCell>
        <TableCell align="center">{bay || ''}</TableCell>
        <TableCell align="center">{rack || ''}</TableCell>
        <TableCell align="center">{price || ''}</TableCell>
        <TableCell align="center">{cost || ''}</TableCell>
        <TableCell align="center">{quantity || ''}</TableCell>
        <TableCell align="center">{physical_qty || ''}</TableCell>
        <TableCell align="center">{diff || ''}</TableCell>
        <TableCell align="center">{piece_qty || ''}</TableCell>
        <TableCell align="center">{physical_piece_qty || ''}</TableCell>
        <TableCell align="center">{piece_diff || ''}</TableCell>
        <TableCell>
          <TextField
            className="table-cell-style"
            value={remark || ''}
            disabled
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f5f7fa",
                fontSize: "0.75rem",
                height: "28px",
                "&.Mui-disabled": {
                  backgroundColor: "#f5f7fa",
                  color: "#212b36",
                },
                "& .MuiOutlinedInput-input": {
                  py: 0.3,
                },
              },
            }}
          />
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={2} display="flex" justifyContent="space-between" alignItems="center">
              <Grid2 size={{ xs: 12, md: 7 }}>
                <Typography className="page-title">
                  Stock Take History
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField
                  className="input-border"
                  placeholder="Search by product name, bay, rack, or remark..."
                  value={searchQuery}
                  size="small"
                  fullWidth
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: "#637381" }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchQuery('');
                            }}
                            edge="end"
                          >
                            <Iconify icon="eva:close-fill" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f7fa",
                      fontSize: "0.875rem",
                      "&:hover": {
                        backgroundColor: "#fff"
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff"
                      }
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 1 }}>
                <Button
                  variant="contained"
                  className="button-variant-contained"
                  startIcon={<Iconify icon="eva:download-fill" width={16} />}
                  onClick={handleExportExcel}
                  fullWidth
                >
                  Export
                </Button>
              </Grid2>
            </Grid2>
          </Box>

          <TableContainer className="table-container">
            <Table
              size="small"
              className="table-cell-border"
            >
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell sx={{ width: "280px", minWidth: "280px" }}>
                    Product Code - Name
                  </TableCell>
                  <TableCell align="center" sx={{ width: "80px", minWidth: "80px" }}>
                    Bay
                  </TableCell>
                  <TableCell align="center" sx={{ width: "70px", minWidth: "70px" }}>
                    Rack
                  </TableCell>
                  <TableCell align="center">
                    Sale P
                  </TableCell>
                  <TableCell align="center" >
                    Cost P
                  </TableCell>
                  <TableCell align="center" sx={{ width: "80px", minWidth: "80px" }}>
                    Sys Qty
                  </TableCell>
                  <TableCell align="center" sx={{ width: "80px", minWidth: "80px" }}>
                    Phy Qty
                  </TableCell>
                  <TableCell align="center">
                    Diff
                  </TableCell>
                  <TableCell align="center" >
                    Piece Qty
                  </TableCell>
                  <TableCell align="center" sx={{ width: "120px", minWidth: "120px" }}>
                    Phy Piece Qty
                  </TableCell>
                  <TableCell align="center" >
                    Piece Diff
                  </TableCell>
                  <TableCell>
                    Remark
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHistory.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      align="center"
                      sx={{
                        py: 6,
                        borderBottom: "1px dashed rgba(145, 158, 171, 0.16)",
                        borderBottomStyle: "dashed",
                        "& .MuiTableCell-root :last-child": {
                          borderBottom: "1px solid rgba(145, 158, 171, 0.16)",
                          borderBottomStyle: "dashed",
                        },
                      }}
                    >
                      <Typography
                        className="table-cell-style"

                      >
                        {stockTakeHistory.length === 0
                          ? "No stock take history data available."
                          : "No results found matching your search."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredHistory
                    .slice(table.page * table.rowsPerPage, table.page * table.rowsPerPage + table.rowsPerPage)
                    .map((item, index) => renderRow(item, index))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ pt: 2 }}>
            <TablePaginationCustom
              className="pagination-styles"
              page={table.page}
              dense={table.dense}
              count={filteredHistory.length}
              rowsPerPage={table.rowsPerPage}
              onPageChange={table.onChangePage}
              onRowsPerPageChange={table.onChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default StockTakeHistoryList;
